import { useState, useEffect } from "react";

export type CardData = Record<string, any>;

interface UseCardDataOptions {
  /**
   * If true, fetches card data immediately on mount.
   * If false, you must call refetch() manually.
   */
  enabled?: boolean;
  /**
   * Game to fetch card data for
   */
  game?: string;
}

interface UseCardDataResult {
  cardData: CardData;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch card data from the API
 * Replaces static imports of card_data.json
 *
 * @example
 * const { cardData, isLoading, error } = useCardData({ game: "one-piece" });
 */
export function useCardData(
  options: UseCardDataOptions = {},
): UseCardDataResult {
  const { enabled = true, game = "one-piece" } = options;
  const [cardData, setCardData] = useState<CardData>({});
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchCardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
      const secretKey = process.env.NEXT_PUBLIC_BACKEND_SECRET_KEY;

      if (!secretKey) {
        throw new Error("NEXT_PUBLIC_BACKEND_SECRET_KEY not configured");
      }

      const params = new URLSearchParams({
        game,
        API_KEY: secretKey,
      });

      const response = await fetch(
        `${apiUrl}/api/data/card-data?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch card data: ${response.status}`);
      }

      const data = await response.json();
      setCardData(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      console.error("Error fetching card data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchCardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, game]);

  return {
    cardData,
    isLoading,
    error,
    refetch: fetchCardData,
  };
}
